import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const DEBUG = new URLSearchParams(window.location.search).has('debug_gift');

/** @param {string} message */
function debugLog(message) {
  if (!DEBUG) return;
  console.log('[cart-free-gift]', message);
  let panel = document.getElementById('cart-free-gift-debug');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'cart-free-gift-debug';
    panel.style.cssText =
      'position:fixed;bottom:10px;left:10px;z-index:2147483647;background:#000;color:#0f0;' +
      'font:11px/1.4 monospace;padding:10px;max-width:360px;max-height:280px;overflow:auto;' +
      'white-space:pre-wrap;border-radius:6px;box-shadow:0 4px 20px rgba(0,0,0,0.5);';
    document.body.appendChild(panel);
  }
  const line = document.createElement('div');
  line.textContent = new Date().toISOString().slice(11, 23) + '  ' + message;
  panel.appendChild(line);
  panel.scrollTop = panel.scrollHeight;
}

/**
 * Keeps a configured free (0€) product synced as a cart line: added
 * automatically whenever the cart has any other item, and removed if the
 * cart would otherwise contain only the gift. Configured under Theme
 * settings → Cart → "Regalo gratis automático".
 */
class CartFreeGiftSync extends HTMLElement {
  /** @type {boolean} */
  #syncing = false;

  /**
   * In-memory "is the gift currently a cart line" flag, updated after every
   * sync. Mainly bookkeeping for `#sync()`'s own fetch; `null` until the
   * first check completes.
   * @type {boolean | null}
   */
  #giftPresent = null;

  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.handle = this.dataset.handle;
    debugLog(`connected. variantId=${this.variantId} handle=${this.handle}`);
    if (!this.variantId || !this.handle) {
      debugLog('ABORT: missing variantId or handle — check the gift product handle setting.');
      return;
    }

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);

    // Cover the initial page load / navigation case too.
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /** @param {CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    debugLog(`cartLinesUpdate received. action=${event.action} target===this? ${event.target === this}`);

    // Ignore the events we dispatch ourselves (avoids re-triggering off our
    // own add/remove — the resulting state is already correct by then).
    if (event.target === this) {
      debugLog('ignoring: this is our own dispatched event.');
      return;
    }

    // Wait for the triggering mutation to actually finish before reacting —
    // firing the gift request in parallel with it raced two concurrent
    // /cart/add.js calls and could leave the drawer's rendered HTML
    // reflecting whichever one the server happened to process last,
    // sometimes missing a line until the next full reload. Sequential is a
    // little slower but reliable; the redundant /cart.js re-check (not the
    // sequencing) was the slow part before, and that's still skipped below
    // by reading the item list straight off the event's own promise.
    if (event.promise) {
      debugLog('awaiting event.promise...');
      event.promise
        .then(({ detail }) => {
          debugLog(`event.promise resolved. detail.items present? ${Boolean(detail?.items)} (count=${detail?.items?.length ?? 'n/a'})`);
          if (detail?.items) {
            this.#syncFromItems(detail.items);
          } else {
            debugLog('falling back to #sync() (no detail.items on this event)');
            this.#sync();
          }
        })
        .catch((error) => debugLog('event.promise REJECTED: ' + error));
    } else {
      debugLog('no event.promise, falling back to #sync()');
      this.#sync();
    }
  };

  /** @param {Array<{ handle: string }>} items */
  async #syncFromItems(items) {
    debugLog(
      `syncFromItems: items=[${items.map((i) => i.handle).join(', ')}] syncing=${this.#syncing}`
    );
    if (this.#syncing) {
      debugLog('skip: already syncing.');
      return;
    }

    const giftLine = items.find((item) => item.handle === this.handle);
    const hasOtherItems = items.some((item) => item.handle !== this.handle);
    this.#giftPresent = Boolean(giftLine);
    debugLog(`giftLine present=${Boolean(giftLine)} hasOtherItems=${hasOtherItems}`);

    this.#syncing = true;
    try {
      if (hasOtherItems && !giftLine) {
        debugLog('-> calling #addGift()');
        await this.#addGift();
      } else if (!hasOtherItems && giftLine) {
        debugLog('-> calling #removeGift()');
        await this.#removeGift(giftLine);
      } else {
        debugLog('-> no action needed.');
      }
    } finally {
      this.#syncing = false;
    }
  }

  async #sync() {
    if (this.#syncing) return;
    this.#syncing = true;

    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) =>
        response.json()
      );

      const giftLine = cart.items.find((item) => item.handle === this.handle);
      const hasOtherItems = cart.items.some((item) => item.handle !== this.handle);
      this.#giftPresent = Boolean(giftLine);

      if (hasOtherItems && !giftLine) {
        await this.#addGift();
      } else if (!hasOtherItems && giftLine) {
        await this.#removeGift(giftLine);
      }
    } catch (error) {
      console.warn('[cart-free-gift] sync failed:', error);
    } finally {
      this.#syncing = false;
    }
  }

  #getCartSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];
    const found = document.querySelectorAll('cart-items-component');
    found.forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sectionIds.push(element.dataset.sectionId);
      }
    });
    debugLog(`getCartSectionIds: found ${found.length} <cart-items-component> element(s), sectionIds=[${sectionIds.join(', ')}]`);
    return sectionIds;
  }

  async #addGift() {
    debugLog('addGift: starting...');
    const sectionIds = this.#getCartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: this.variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.variantId, quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();
      debugLog(
        `addGift: response ok=${response.ok} status=${response.status} data.status=${data.status} sections keys=[${Object.keys(data.sections || {}).join(', ')}]`
      );

      if (!response.ok || data.status) {
        debugLog('addGift: FAILED — ' + (data.message || data.description || JSON.stringify(data)));
        this.#giftPresent = false;
        deferred.reject(new Error(data.message || 'cart-free-gift: add failed'));
        return;
      }

      // Show the result right away — /cart/add.js's own response already has
      // the rendered section HTML, no need to wait on anything else for this.
      debugLog('addGift: success, morphing sections now.');
      this.#morphSections(sectionIds, data.sections);

      // /cart/add.js's response is a line-item object, not a full cart (no
      // `currency` field), which crashes createCartFromAjaxResponse if fed
      // straight in. Fetch the real cart separately just to resolve the
      // event correctly for other listeners (e.g. the header cart bubble).
      const freshCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      debugLog('addGift: fresh /cart.js fetched for event resolution, item_count=' + freshCart.item_count);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
        detail: {
          sections: data.sections,
          items: freshCart.items,
          itemCount: freshCart.item_count,
          source: 'cart-free-gift',
        },
      });
    } catch (error) {
      debugLog('addGift: EXCEPTION — ' + error);
      this.#giftPresent = false;
      deferred.reject(error);
    }
  }

  /** @param {{ key: string }} giftLine */
  async #removeGift(giftLine) {
    const sectionIds = this.#getCartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: giftLine.key, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: giftLine.key,
          quantity: 0,
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();
      debugLog(`removeGift: response ok=${response.ok} status=${response.status} data.status=${data.status}`);

      if (!response.ok || data.status) {
        debugLog('removeGift: FAILED — ' + (data.message || JSON.stringify(data)));
        this.#giftPresent = true;
        deferred.reject(new Error(data.message || 'cart-free-gift: remove failed'));
        return;
      }

      // Unlike /cart/add.js, /cart/change.js's response is already a full
      // cart object (has `currency` etc.), so it's safe to use directly here.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { sections: data.sections, items: data.items, source: 'cart-free-gift' },
      });

      debugLog('removeGift: success, morphing sections now.');
      this.#morphSections(sectionIds, data.sections);
    } catch (error) {
      debugLog('removeGift: EXCEPTION — ' + error);
      this.#giftPresent = true;
      deferred.reject(error);
    }
  }

  /**
   * @param {string[]} sectionIds
   * @param {Record<string, string> | undefined} sectionsHtml
   */
  #morphSections(sectionIds, sectionsHtml) {
    if (!sectionsHtml) {
      debugLog('morphSections: no sectionsHtml on the response at all — nothing to morph.');
      return;
    }
    for (const sectionId of sectionIds) {
      const html = sectionsHtml[sectionId];
      debugLog(`morphSections: sectionId=${sectionId} html present? ${Boolean(html)} (length=${html?.length ?? 0})`);
      if (html) morphSection(sectionId, html, { mode: 'hydration' });
    }
  }
}

if (!customElements.get('cart-free-gift-sync')) {
  customElements.define('cart-free-gift-sync', CartFreeGiftSync);
}
